import { FC, ReactNode } from 'react';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';

// components
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedContactDealerButton } from '../PdpSharedButtons/PdpSharedContactDealerButton';
import { PdpSharedMakeOfferButton } from '../PdpSharedButtons/PdpSharedMakeOfferButton';
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import { PdpSharedDeposit } from '../PdpSharedDeposit/PdpSharedDeposit';
import { addPageAction } from '../../utils/tracking/rumTracking';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import { FollowSearch } from '../pdpMessages';
import { PdpSharedShoppingButtons } from '../shared/PdpSharedShoppingButtons/PdpSharedShoppingButtons';
import { PdpSharedDelimiter } from '../shared/PdpSharedDelimiter/PdpSharedDelimiter';

// helpers
import classNames from 'classnames';
import { getDisplayInfo } from '../helpers/getDisplayInfo';
import {
    locations,
    trackFollowSearchAdded,
    trackFollowSearchRemoved,
} from '../../utils/tracking/shared/followSearchTracking';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespSidebarButtons.scss';

import { PdpRespSidebarButtons_user$data } from './__generated__/PdpRespSidebarButtons_user.graphql';
import { PdpRespSidebarButtons_item$data } from './__generated__/PdpRespSidebarButtons_item.graphql';
import { PdpRespSidebarButtons_itemSearch$data } from './__generated__/PdpRespSidebarButtons_itemSearch.graphql';
import { PdpRespSidebarButtons_displayInfo$data } from './__generated__/PdpRespSidebarButtons_displayInfo.graphql';
import { PdpRespSidebarButtons_viewer$data } from './__generated__/PdpRespSidebarButtons_viewer.graphql';

type Props = {
    viewer: PdpRespSidebarButtons_viewer$data;
    user: PdpRespSidebarButtons_user$data | null;
    item: PdpRespSidebarButtons_item$data;
    itemSearch: PdpRespSidebarButtons_itemSearch$data | null;
    displayInfo: PdpRespSidebarButtons_displayInfo$data | null;
    relayVariables: { itemId: string; userId?: string | null; isMultiSku?: boolean };
    isClient?: boolean;
    relay: RelayProp;
    quantitySelected?: string;
    currency?: string;
    skuId?: string;
};

// messages
const followSearchText = (searchName: string): ReactNode => (
    <p className={styles.followSearchText}>
        <FollowSearch searchName={searchName} searchNameClassname={styles.searchName} />
    </p>
);

const PdpRespSidebarButtonsComponent: FC<Props> = props => {
    const { viewer, relayVariables, user, item, quantitySelected, skuId, itemSearch } = props;
    const { itemId, userId, isMultiSku } = relayVariables;
    const { isMultiItemCart } = viewer;

    const displayInfo = getDisplayInfo({
        displayInfo: props.displayInfo,
        skus: item.skus,
        selectedSkuId: skuId,
        isMultiSku,
    });

    const { isSold: isSoldPdp, isUnpurchasable, saveSearchData } = item || {};
    const { isVerifiedTrade, favorites } = user || {};

    if (!displayInfo?.ctas || isSoldPdp || isUnpurchasable) {
        return null;
    }

    const { showPurchase, showMakeOffer, showRequestPrice, showFollowCategory, showAcceptOffer } =
        displayInfo.ctas;
    const buttonsClass = classNames({ [styles.hasQuantitySelect]: quantitySelected });

    const MakeOfferButton: FC = () =>
        showMakeOffer ? (
            <CurrencyProvider
                render={({ currency }: { currency: string }) => (
                    <PdpSharedMakeOfferButton
                        itemId={itemId}
                        selectedSkuId={skuId}
                        item={item}
                        userId={userId}
                        user={user}
                        quantitySelected={quantitySelected}
                        currency={currency}
                        hasPrivateOffer={showAcceptOffer}
                        showArrow
                        gaLabel="side rail cta"
                    />
                )}
            />
        ) : null;

    const followSearchName = saveSearchData?.name;
    const followSearchPage = saveSearchData?.page;

    addPageAction('respPdpCTAsRendered', { itemId });

    // secondary CTAs are the buttons below the title
    let secondaryCtas: ReactNode;

    if (showRequestPrice) {
        // Request price CTA is exclusive to the rest, so display it alone if showRequestPrice
        secondaryCtas = (
            <PdpSharedContactDealerButton
                type={'REQUEST_PRICE'}
                item={item}
                selectedSkuId={skuId}
                user={user}
            />
        );
    } else if (isMultiItemCart) {
        // If multi item cart is enabled, display purchase and make offer buttons
        secondaryCtas = (
            <div className={classNames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapXxsmall)}>
                {showPurchase && (
                    <PdpSharedShoppingButtons
                        user={user}
                        item={item}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                    />
                )}
                {showPurchase && showMakeOffer && <PdpSharedDelimiter />}
                <MakeOfferButton />
            </div>
        );
    } else {
        // Otherwise just display buttons as is
        secondaryCtas = (
            <>
                {showPurchase && (
                    <PdpSharedPurchaseButton
                        itemId={itemId}
                        item={item}
                        userId={userId}
                        quantitySelected={quantitySelected}
                        skuId={skuId}
                    />
                )}
                <MakeOfferButton />
            </>
        );
    }

    return (
        <div className={buttonsClass} data-tn="item-actions">
            {secondaryCtas}

            <PdpSharedDeposit userId={userId} item={item} />

            {/* Follow Search */}
            {showFollowCategory && followSearchName && isVerifiedTrade ? (
                // trade users
                <div className={styles.followTrade}>
                    {followSearchText(followSearchName)}
                    <div>
                        <SaveSearchButton
                            size="small"
                            className={styles.followButtonTrade}
                            userId={userId}
                            favoritesConnection={favorites}
                            page={followSearchPage}
                            name={followSearchName}
                            location={isSoldPdp && locations.SOLD_PDP}
                            onFollowSearchCompleted={(isFollowing: boolean): void =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                            itemSearch={itemSearch}
                        />
                    </div>
                </div>
            ) : (
                showFollowCategory &&
                followSearchName && (
                    // non trade users
                    <div>
                        {followSearchText(followSearchName)}
                        <SaveSearchButton
                            userId={userId}
                            user={user}
                            favoritesConnection={favorites || null}
                            page={followSearchPage}
                            name={followSearchName}
                            fullWidth
                            location={isSoldPdp && locations.SOLD_PDP}
                            onFollowSearchCompleted={(isFollowing: boolean): void =>
                                isFollowing
                                    ? trackFollowSearchAdded({ location: locations.SOLD_PDP })
                                    : trackFollowSearchRemoved({ location: locations.SOLD_PDP })
                            }
                            itemSearch={itemSearch}
                        />
                    </div>
                )
            )}
        </div>
    );
};

export const PdpRespSidebarButtons = createFragmentContainer(PdpRespSidebarButtonsComponent, {
    viewer: graphql`
        fragment PdpRespSidebarButtons_viewer on Viewer {
            isMultiItemCart: featureFlag(feature: "multiItemCart")
        }
    `,
    user: graphql`
        fragment PdpRespSidebarButtons_user on User {
            isVerifiedTrade
            ...PdpSharedContactDealerButton_user
            ...PdpSharedMakeOfferButton_user
            favorites(first: 1, types: [SEARCH, USER_PREFERENCE], pages: $followSearchPages)
                @include(if: $fetchFollowSearch) {
                ...SaveSearchButton_favoritesConnection
            }
            ...PdpSharedShoppingButtons_user
        }
    `,
    item: graphql`
        fragment PdpRespSidebarButtons_item on Item {
            isSold
            isUnpurchasable
            saveSearchData {
                name
                page
            }
            ...PdpSharedContactDealerButton_item
            ...PdpSharedPurchaseButton_item
            ...PdpSharedMakeOfferButton_item
            ...PdpSharedDeposit_item
            skus @include(if: $isMultiSku) {
                serviceId
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showPurchase
                        showMakeOffer
                        showRequestPrice
                        showFollowCategory
                        showAcceptOffer
                    }
                }
            }
            ...PdpSharedShoppingButtons_item
        }
    `,
    itemSearch: graphql`
        fragment PdpRespSidebarButtons_itemSearch on ItemSearchQueryConnection {
            ...SaveSearchButton_itemSearch
        }
    `,
    displayInfo: graphql`
        fragment PdpRespSidebarButtons_displayInfo on ItemDisplayInfo {
            ctas {
                showPurchase
                showMakeOffer
                showRequestPrice
                showFollowCategory
                showAcceptOffer
            }
        }
    `,
});
